import React, { useState, useRef, useCallback, useEffect } from "react";
import Webcam from "react-webcam";
import {
  DocumentAnalysisClient,
  AzureKeyCredential,
} from "@azure/ai-form-recognizer";
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Box,
  Button,
  TextField,
  CircularProgress,
  useMediaQuery,
  Grid, 
  Card, 
  CardContent,
  Divider
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { PDFDocument } from "pdf-lib";
import { Dialog, DialogTitle, DialogActions, DialogContent, DialogContentText } from '@mui/material';

function App() {
  const [step, setStep] = useState("idle");
  const [frontImage, setFrontImage] = useState(null);
  const [backImage, setBackImage] = useState(null);
  const [hasBackSide, setHasBackSide] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [data, setData] = useState(null);
  const [editableData, setEditableData] = useState({});
  const [backDialog, setBackDialog] = useState(false);

  const webcamRef = useRef(null);

  // Replace with your own key and endpoint (in a real app, keep these on the backend)
  const azureKey = "EymXIvUywmtQC2cBUBJEpaXBQHua4OLuWfD31TsBVHOoXzogtQGrJQQJ99BAACYeBjFXJ3w3AAALACOGtTvX";
  const azureEndpoint = "https://bcards.cognitiveservices.azure.com/";


  // Check if device is mobile
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // STEP: Start scanning
  const handleStartScan = () => {
    setStep("frontCamera");
  };

  // Screenshot from webcam
  const captureImage = useCallback(() => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      // Ensure we're getting a good quality image
      if (imageSrc) {
        const img = new Image();
        img.src = imageSrc;
        return imageSrc;
      }
    }
    return null;
  }, []);

  // Capture front
  const handleCaptureFront = () => {
    const image = captureImage();
    if (image) {
      setFrontImage(image);
      setStep("idle");
      setBackDialog(true);
    }
  };
  const downloadFile = (content, fileName, contentType) => {
    const a = document.createElement('a');
    const file = new Blob([content], { type: contentType });
    a.href = URL.createObjectURL(file);
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // If user picks "Yes" for backside
  useEffect(() => {
    if (hasBackSide) {
      setStep("backCamera");
    } else if (frontImage) {
      setStep("confirmImages");
    }
  }, [hasBackSide, frontImage]);

  // Capture back
  const handleCaptureBack = () => {
    const image = captureImage();
    if (image) {
      setBackImage(image);
      setStep("confirmImages");
    }
  };

  // Confirm images => Analyze
  const handleConfirmImages = async () => {
    setStep("analyzing");
    setAnalyzing(true);
    await analyzeBusinessCard();
    setAnalyzing(false);
  };

  // Convert base64 to Uint8Array
  const base64ToUint8Array = (base64) => {
    const base64String = base64.split(",")[1];
    const binaryString = window.atob(base64String);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  };

  // Analyze business card with Azure
  const analyzeBusinessCard = async () => {
    try {
      // 1) Create a PDF with proper dimensions
      const pdfDoc = await PDFDocument.create();
      
      // 2) Function to create page and embed image
      const addImageToPage = async (imageData) => {
        if (!imageData) return;
        const bytes = base64ToUint8Array(imageData);
        const image = await pdfDoc.embedJpg(bytes);
        const page = pdfDoc.addPage([image.width, image.height]);
        page.drawImage(image, {
          x: 0,
          y: 0,
          width: image.width,
          height: image.height,
        });
      };
  
      // 3) Add both images
      await addImageToPage(frontImage);
      if (backImage) {
        await addImageToPage(backImage);
      }
  
      // 4) Convert to bytes
      const pdfBytes = await pdfDoc.save();
  
      // 5) Initialize Azure client
      const client = new DocumentAnalysisClient(
        azureEndpoint,
        new AzureKeyCredential(azureKey)
      );
  
      // 6) Analyze document
      const poller = await client.beginAnalyzeDocument(
        "prebuilt-businessCard",
        pdfBytes
      );
      const result = await poller.pollUntilDone();
  
      // 7) Process results
      if (result.documents && result.documents.length > 0) {
        // Initialize arrays to store all values
        let allContactNames = [];
        let allCompanyNames = [];
        let allAddresses = [];
        let allEmails = [];
        let allPhones = [];
        let allJobTitles = [];
        let allWebsites = [];
        let allFaxes = [];
  
        // Combine data from all documents
        result.documents.forEach(doc => {
          const fields = doc.fields;
          
          // Safely add values from each field
          if (fields.ContactNames?.values) allContactNames.push(...fields.ContactNames.values);
          if (fields.CompanyNames?.values) allCompanyNames.push(...fields.CompanyNames.values);
          if (fields.Addresses?.values) allAddresses.push(...fields.Addresses.values);
          if (fields.Emails?.values) allEmails.push(...fields.Emails.values);
          if (fields.WorkPhones?.values) allPhones.push(...fields.WorkPhones.values);
          if (fields.JobTitles?.values) allJobTitles.push(...fields.JobTitles.values);
          if (fields.Websites?.values) allWebsites.push(...fields.Websites.values);
          if (fields.Faxes?.values) allFaxes.push(...fields.Faxes.values);
        });
  
        // Create the final data object
        const bcData = {
          contactNames: allContactNames.map(c => c.content || ''),
          companyNames: allCompanyNames.map(c => c.content || ''),
          addresses: allAddresses.map(a => a.content || ''),
          emails: allEmails.map(e => e.content || ''),
          phones: allPhones.map(p => p.content || ''),
          jobTitles: allJobTitles.map(j => j.content || ''),
          websites: allWebsites.map(w => w.content || ''),
          faxNumbers: allFaxes.map(f => f.content || ''),
        };
  
        console.log('Extracted Data:', bcData);
        setData(bcData);
        setEditableData(bcData);
        setStep("results");
      } else {
        alert("No business card data found in the images.");
        setStep("idle");
      }
    } catch (error) {
      console.error("Error analyzing business card:", error);
      alert("Error analyzing business card. Check console for details.");
      setStep("idle");
    }
  };

  // Handle field changes in the UI
  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setEditableData((prev) => ({ ...prev, [name]: value }));
  };

  // Save & Reset
  const handleSaveAndReset = () => {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const baseFileName = `business-card-${timestamp}`;

    // Save the JSON data
    const finalData = {
      ...editableData,
      timestamp: new Date().toISOString()
    };
    const jsonBlob = new Blob([JSON.stringify(finalData, null, 2)], { type: 'application/json' });
    const jsonUrl = URL.createObjectURL(jsonBlob);
    const jsonLink = document.createElement('a');
    jsonLink.href = jsonUrl;
    jsonLink.download = `${baseFileName}.json`;
    document.body.appendChild(jsonLink);
    jsonLink.click();
    document.body.removeChild(jsonLink);
    URL.revokeObjectURL(jsonUrl);

    // Save the front image
    if (frontImage) {
      const frontLink = document.createElement('a');
      frontLink.href = frontImage; // Use the base64 URL directly
      frontLink.download = `${baseFileName}-front.jpg`;
      document.body.appendChild(frontLink);
      frontLink.click();
      document.body.removeChild(frontLink);
    }

    // Save the back image
    if (backImage) {
      const backLink = document.createElement('a');
      backLink.href = backImage; // Use the base64 URL directly
      backLink.download = `${baseFileName}-back.jpg`;
      document.body.appendChild(backLink);
      backLink.click();
      document.body.removeChild(backLink);
    }

    // Reset the state
    setFrontImage(null);
    setBackImage(null);
    setHasBackSide(false);
    setData(null);
    setEditableData({});
    setStep("idle");
  };

  // Build dynamic constraints for the camera:
  // Try "environment" if isMobile, otherwise fallback to "user"
  const videoConstraints = isMobile
    ? { facingMode: { ideal: "environment" } }
    : { facingMode: "user" };

  // Add some logs or a callback to see if there's an error
  const handleCameraError = (err) => {
    console.error("Camera Error:", err);
    alert("Could not access the camera. Make sure you have given permission.");
  };

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: "#f5f5f5", height: "100vh" }}>
      {/* Top Bar */}

      <Box
        sx={{
          width: "100%",
          backgroundColor: "#ffffff",
          borderBottom: "1px solid #ccc",
          pt: 3,
          textAlign: "center",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold", color: "#f88b4e" }}>
          DezineXpert
        </Typography>
        <Typography variant="subtitle1" sx={{ color: "#181934", mt: 1 }}>
          AI Assistant for Interior & Construction Projects
        </Typography>
      </Box>

      <Container
        maxWidth="sm"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 3,
        }}
      >
        <Dialog
          open={backDialog}
          onClose={() => {
            setBackDialog(false);
            setHasBackSide(false);
            setStep("confirmImages");
          }}
        >
          <DialogTitle>Back Side Check</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Does this business card have information on the back side?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setBackDialog(false);
              setHasBackSide(false);
              setStep("confirmImages");
            }}>
              No
            </Button>
            <Button onClick={() => {
              setBackDialog(false);
              setHasBackSide(true);
            }} autoFocus>
              Yes
            </Button>
          </DialogActions>
        </Dialog>
        {step === "idle" && (
         <Container maxWidth="lg" sx={{ mt: 5 }}>
         <Card
           sx={{
             boxShadow: 3,
             borderRadius: 3,
             overflow: "hidden",
           }}
         >
           <CardContent>
             <Grid container spacing={4} alignItems="center">
               {/* Left Section */}
               <Grid item xs={12} md={5}>
                 <Box
                   sx={{
                     display: "flex",
                     flexDirection: "column",
                     alignItems: "center",
                     justifyContent: "center",
                     gap: 2,
                     textAlign: "center",
                   }}
                 >
                   <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                     Join WhatsApp Community
                   </Typography>
                   {/* QR Code */}
                   <img
                     src="wa-qrcode.png"
                     alt="QR Code"
                     style={{
                       width: "200px",
                       height: "200px",
                       padding: "10px",
                     }}
                   />
                   <Typography variant="body1" sx={{ color: "#555" }}>
                     WhatsApp: <b>+91 99520 459 32</b>
                   </Typography>
                 </Box>
               </Grid>
 
               {/* Divider Section */}
               <Grid item xs={12} md={2}>
                 <Box
                   sx={{
                     display: "flex",
                     flexDirection: "column",
                     alignItems: "center",
                     justifyContent: "center",
                   }}
                 >
                   <Divider
                     orientation="vertical"
                     flexItem
                     sx={{
                       height: "100%",
                       position: "relative",
                     }}
                   >
                     <Typography
                       sx={{
                         position: "absolute",
                         top: "50%",
                         left: "50%",
                         transform: "translate(-50%, -50%)",
                         backgroundColor: "#fff",
                         px: 1,
                         fontWeight: "bold",
                         color: "#555",
                       }}
                     >
                       or
                     </Typography>
                   </Divider>
                 </Box>
               </Grid>
 
               {/* Right Section */}
               <Grid item xs={12} md={5}>
                 <Box
                   sx={{
                     display: "flex",
                     flexDirection: "column",
                     alignItems: "center",
                     justifyContent: "center",
                     gap: 2,
                     textAlign: "center",
                   }}
                 >
                   <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                     Scan your Visiting Card
                   </Typography>
                   <Typography variant="body1" sx={{ color: "#555", mb: 2 }}>
                     Place your visiting card in front of the camera and scan it now.
                   </Typography>
                   <Button
                     variant="contained"
                     sx={{
                       backgroundColor: "#f88b4e",
                       color: "#fff",
                       px: 4,
                       py: 1.5,
                       borderRadius: "30px",
                       fontSize: "16px",
                     }}
                     onClick={handleStartScan}
                   >
                     Scan Now
                   </Button>
                 </Box>
               </Grid>
             </Grid>
           </CardContent>
         </Card>
       </Container>
          // <Button variant="contained" onClick={handleStartScan}>
          //   Scan New Card
          // </Button>
        )}

        {step === "frontCamera" && (
          <>
            <Typography variant="body1">
              Show the Front side of the card to the camera
            </Typography>
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              videoConstraints={videoConstraints}
              onUserMediaError={handleCameraError}
              style={{
                width: "100%",
                borderRadius: 10,
                border: "1px solid #ccc",
              }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleCaptureFront}
            >
              Capture Front
            </Button>
          </>
        )}

        {step === "backCamera" && (
          <>
            <Typography variant="body1">
              Show the Back side of the card to the camera
            </Typography>
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              videoConstraints={videoConstraints}
              onUserMediaError={handleCameraError}
              style={{
                width: "100%",
                borderRadius: 10,
                border: "1px solid #ccc",
              }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleCaptureBack}
            >
              Capture Back
            </Button>
          </>
        )}

        {step === "confirmImages" && (
          <>
            <Typography variant="h6">Confirm Images</Typography>
            <Box>
              {frontImage && (
                <>
                  <Typography variant="subtitle2">Front Image</Typography>
                  <img
                    src={frontImage}
                    alt="Front"
                    style={{
                      width: "100%",
                      border: "1px solid #ccc",
                      borderRadius: 6,
                    }}
                  />
                </>
              )}
              {backImage && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2 }}>
                    Back Image
                  </Typography>
                  <img
                    src={backImage}
                    alt="Back"
                    style={{
                      width: "100%",
                      border: "1px solid #ccc",
                      borderRadius: 6,
                    }}
                  />
                </>
              )}
            </Box>
            <Button variant="contained" color="success" onClick={handleConfirmImages}>
              Analyze Card
            </Button>
          </>
        )}

        {step === "analyzing" && analyzing && (
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress />
            <Typography variant="body1">Analyzing your card...</Typography>
          </Box>
        )}

        {step === "results" && data && (
          <Box sx={{ width: '100%' }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
              Business Card Details
            </Typography>

            {/* Personal Information Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" color="primary" gutterBottom>
                Personal Information
              </Typography>
              {editableData.contactNames?.map((name, index) => (
                <TextField
                  key={`name-${index}`}
                  fullWidth
                  label="Full Name"
                  variant="outlined"
                  value={name || ""}
                  onChange={(e) => {
                    const newNames = [...editableData.contactNames];
                    newNames[index] = e.target.value;
                    setEditableData(prev => ({ ...prev, contactNames: newNames }));
                  }}
                  sx={{ mb: 2 }}
                />
              ))}

              {editableData.jobTitles?.map((title, index) => (
                <TextField
                  key={`jobTitles-${index}`}
                  fullWidth
                  label="Job Title"
                  variant="outlined"
                  value={title || ""}
                  onChange={(e) => {
                    const newTitles = [...editableData.jobTitles];
                    newTitles[index] = e.target.value;
                    setEditableData(prev => ({ ...prev, jobTitles: newTitles }));
                  }}
                  sx={{ mb: 2 }}
                />
              ))}
            </Box>

            {/* Company Information Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" color="primary" gutterBottom>
                Company Information
              </Typography>
              {editableData.companyNames?.map((company, index) => (
                <TextField
                  key={`company-${index}`}
                  fullWidth
                  label="Company Name"
                  variant="outlined"
                  value={company || ""}
                  onChange={(e) => {
                    const newCompanies = [...editableData.companyNames];
                    newCompanies[index] = e.target.value;
                    setEditableData(prev => ({ ...prev, companyNames: newCompanies }));
                  }}
                  sx={{ mb: 2 }}
                />
              ))}

              {editableData.addresses?.map((address, index) => (
                <TextField
                  key={`address-${index}`}
                  fullWidth
                  label="Address"
                  variant="outlined"
                  multiline
                  rows={2}
                  value={address || ""}
                  onChange={(e) => {
                    const newAddresses = [...editableData.addresses];
                    newAddresses[index] = e.target.value;
                    setEditableData(prev => ({ ...prev, addresses: newAddresses }));
                  }}
                  sx={{ mb: 2 }}
                />
              ))}
            </Box>

            {/* Contact Information Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" color="primary" gutterBottom>
                Contact Information
              </Typography>
              {editableData.phones?.map((phone, index) => (
                <TextField
                  key={`phones-${index}`}
                  fullWidth
                  label="Phone Number"
                  variant="outlined"
                  value={phone || ""}
                  onChange={(e) => {
                    const newPhones = [...editableData.phones];
                    newPhones[index] = e.target.value;
                    setEditableData(prev => ({ ...prev, phones: newPhones }));
                  }}
                  sx={{ mb: 2 }}
                />
              ))}

              {editableData.emails?.map((email, index) => (
                <TextField
                  key={`emails-${index}`}
                  fullWidth
                  label="Email Address"
                  variant="outlined"
                  value={email || ""}
                  onChange={(e) => {
                    const newEmails = [...editableData.emails];
                    newEmails[index] = e.target.value;
                    setEditableData(prev => ({ ...prev, emails: newEmails }));
                  }}
                  sx={{ mb: 2 }}
                />
              ))}

              {editableData.websites?.map((website, index) => (
                <TextField
                  key={`websites-${index}`}
                  fullWidth
                  label="Website"
                  variant="outlined"
                  value={website || ""}
                  onChange={(e) => {
                    const newWebsites = [...editableData.websites];
                    newWebsites[index] = e.target.value;
                    setEditableData(prev => ({ ...prev, websites: newWebsites }));
                  }}
                  sx={{ mb: 2 }}
                />
              ))}
            </Box>

            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveAndReset}
              sx={{
                mt: 2,
                px: 4,
                py: 1.5,
                borderRadius: 2,
                boxShadow: 2
              }}
            >
              Looks Good
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
}

export default App;


